import { isAfter, closestTo, parseISO, format } from "date-fns";
import * as CONSTANTS from "./constants";

export const cacheBustString = () => {
    // Cache-bust string based on current date and hour
    const date = new Date();
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const hour = date.getHours().toString().padStart(2, "0");

    return `${day}${month}${hour}`;
};

export const getBandsByTicketType = (
    ticketType,
    bands = CONSTANTS.PRICE_BANDS,
) => {
    if (!bands) return;

    const band_map = Object.entries(bands);
    return band_map
        .filter(([key, band]) => band && band["colour"])
        .map(([key, band]) => {
            return {
                band: key,
                amount: band[ticketType],
                color: band["colour"],
            };
        });
};

export const parseSeatStatus = (seatStatusData) => {
    if (!seatStatusData) return;

    const seatStatusMap = {};
    const seatInfoMap = {};

    seatStatusData.split(/\r\n|\n|\r/).forEach((entry) => {
        // Match status lines like A, B, U, W, X
        const status_matches = entry.match(/^([ABUWX]):(.+?):([\d,]+)$/);
        if (status_matches) {
            const [_, status, priceBandOrObj, seatIds] = status_matches;

            let lockType = null;
            let priceBandId = priceBandOrObj;

            // Match any string inside the {...} structure
            const nestedMatch = priceBandOrObj.match(/\{[^:]+:"([^"]+)"\}/);
            if (nestedMatch) {
                lockType = nestedMatch[1];

                // Get outer priceBandId before the {..}
                const mainIdMatch = priceBandOrObj.match(/^([^:]+):\{.+\}$/);
                if (mainIdMatch) {
                    priceBandId = mainIdMatch[1];
                }
            }

            seatIds.split(",").forEach((seatId) => {
                seatStatusMap[seatId] = {
                    priceBandId,
                    status,
                    ...(lockType ? { lockType } : {}),
                };
            });
        }

        // Match info lines like I:INFO:1234
        const info_matches = entry.match(/^([I]):(.+?):([\d,]+)$/);
        if (info_matches) {
            const [_, status, priceBandId, seatIds] = info_matches;

            seatIds.split(",").forEach((seatId) => {
                seatInfoMap[seatId] = {
                    priceBandId,
                    status,
                };
            });
        }
    });

    return { status: seatStatusMap, info: seatInfoMap };
};

export const getTicketsByBand = (band, pricelist) => {
    let prices = pricelist;
    if (!prices) prices = CONSTANTS.INSTANCE_PRICELIST;

    return prices?.filter((price) => {
        return price.priceBand.name == band;
    });
};

export const formatDateTime = (dateTimeStr, separateDateFromTime = false) => {
    if (!dateTimeStr) return "Invalid Date";

    try {
        const date = parseISO(dateTimeStr);

        if (separateDateFromTime) {
            return {
                date: format(date, "EEE d MMM"),
                time: format(date, "h:mma"),
            };
        }
        return format(date, "EEE d MMM h:mma");
    } catch (error) {
        return "Invalid Date";
    }
};

export const getTicketsByInstance = (
    tickets,
    instance_id = CONSTANTS.INSTANCE_ID,
) => {
    return tickets.filter((ticket) => ticket.instance.id == instance_id);
};

export const groupTicketsByTicketType = (tickets) => {
    const grouped_tickets = tickets.reduce((acc, item) => {
        (acc[item.ticketType.name] = acc[item.ticketType.name] || []).push(
            item,
        );
        return acc;
    }, {});

    const mapped_tickets = Object.entries(grouped_tickets).map(
        ([name, tickets]) => {
            return {
                name: name,
                count: tickets.length,
                tickets: tickets,
                seats: tickets
                    .map((ticket) => ticket.seat?.name)
                    .filter((notUndefined) => notUndefined !== undefined),
                seats_group: tickets
                    ?.map((ticket) => ticket.seat?.name)
                    .filter((notUndefined) => notUndefined !== undefined),
                total: (tickets[0].total * tickets.length).toFixed(2),
            };
        },
    );
    return mapped_tickets;
};

export const groupBandsByPrice = (
    bands = CONSTANTS.DEFAULT_TICKET_PRICEBANDS,
) => {
    if (!bands) return;

    const grouped_bands = bands.reduce((acc, item) => {
        (acc[item.amount] = acc[item.amount] || []).push(item);
        return acc;
    }, {});

    const mapped_bands = Object.entries(grouped_bands).map(
        ([amount, bands]) => {
            return {
                amount: amount,
                bands: bands.map((band) => band.band),
            };
        },
    );

    return mapped_bands;
};

export const getBandsByPrice = (bands) => {
    const id_map = bands.reduce((map, item) => {
        map[item.name] = item.id;
        return map;
    }, {});
    const groupedBands = groupBandsByPrice();
    return groupedBands
        ?.map((bandItem) => ({
            ...bandItem,
            band_ids: bandItem.bands
                ?.map((band) => id_map[band])
                .filter((id) => id !== undefined),
        }))
        .sort((a, b) => a.amount - b.amount);
};

export const groupTicketsByInstance = (tickets) => {
    const grouped_tickets = tickets.reduce((acc, item) => {
        (acc[item.instance.id] = acc[item.instance.id] || []).push(item);
        return acc;
    }, {});

    const mapped_tickets = Object.entries(grouped_tickets).map(
        ([id, tickets]) => {
            const ticket = tickets[0];
            return {
                event_id: ticket.event?.id,
                event_name: ticket.event?.name,
                event_venue: ticket.event?.attribute_FestivalKingsStudio,
                event_image: ticket.event?.thumbnailUrl,
                instance_id: id,
                instance_date: formatDateTime(ticket.instance?.start),
                tickets: tickets,
                tickets_total: tickets?.reduce(
                    (sum, item) => sum + item.total,
                    0,
                ),
                seats: tickets
                    ?.map((ticket) => {
                        return {
                            ticket_id: ticket.id,
                            seat_name: ticket.seat?.name,
                            plan_name: ticket.planName,
                        };
                    })
                    .filter((notUndefined) => notUndefined !== undefined),
            };
        },
    );

    return mapped_tickets;
};

export const getGroupedTicketsTotal = (tickets) => {
    // Uses the data from groupTicketsByInstance()
    return tickets?.reduce((sum, ticket) => sum + ticket.tickets_total, 0) || 0;
};

export const getTicketsTotal = (tickets) => {
    return tickets?.reduce((sum, ticket) => sum + ticket.total, 0) || 0;
};

export const getMembershipsTotal = (memberships) => {
    return (
        memberships?.reduce((sum, membership) => sum + membership.total, 0) || 0
    );
};

export const getDonationsTotal = (donations) => {
    return donations?.reduce((sum, donation) => sum + donation.amount, 0) || 0;
};

export const getDeliveryAddress = (data) => {
    if (!data) return;

    const filtered_addresses = data?.customer?.addresses?.filter(
        (address) => address.isDelivery,
    );
    if (!filtered_addresses || !filtered_addresses.length) return;

    const address = filtered_addresses[0];
    return {
        Country: address.country.isoCode,
        AdministrativeDivision: address.administrativeDivision.name,
        Name: address.name,
        Line1: address.line1,
        Line2: address.line2,
        Line3: address.line3,
        Line4: address.line4,
        Line5: address.line5,
        Postcode: address.postcode,
        Town: address.town,
    };
};

export const getDeliveryAmount = (data) => {
    if (!data) return;

    const current_delivery_type = data.ticketDelivery
        ? data.ticketDelivery.type
        : "PrintAtHome";

    const delivery_options = data.ticketDeliveryOptions?.filter(
        (option) => current_delivery_type == option.type,
    );

    return delivery_options?.length ? delivery_options[0].fee.toFixed(2) : null;
};

export const getBasketTicketById = (basketData, id) => {
    const items = basketData?.tickets?.filter((ticket) => {
        return ticket.seat.id == id;
    });
    if (!items || !items.length)
        throw new Error(`No tickets with provided ID are in the basket`);

    return items[0];
};

export const findClosestDate = (arr, dateProp) => {
    const now = new Date();
    const dates = arr.map((item) => parseISO(item[dateProp]));

    const closestDate = closestTo(now, dates);

    return arr.find(
        (item) => parseISO(item[dateProp]).getTime() === closestDate.getTime(),
    );
};

export const getCustomerMembershipData = (customer, membershipsData) => {
    return customer.subscriptions.map((subscription) => {
        const membership_data = membershipsData.find(
            ({ id }) => id == subscription.membership?.id,
        );

        return { ...subscription, ...membership_data };
    });
};

export const getUpcomingTickets = (orders) => {
    const now = new Date();
    return orders.flatMap((order) =>
        order.tickets
            .filter((ticket) => isAfter(parseISO(ticket.instance.start), now))
            .map((ticket) => ({
                ...ticket,
                orderId: order.id,
            })),
    );
};

export const getPastOrders = (orders) => {
    const now = new Date();
    return orders.flatMap((order) =>
        order.tickets.filter((ticket) =>
            isAfter(parseISO(ticket.instance.start), now),
        ),
    );
};

export const getTotalDonations = (orders) => {
    return orders.reduce((sum, order) => {
        const donationSum = order.donations.reduce(
            (acc, donation) => acc + donation.amount,
            0,
        );
        return sum + donationSum;
    }, 0);
};

export const getAudienceTagIds = () => {
    return CONSTANTS.TAGS?.map((tag) => tag.id);
};

export const getCustomerTagIds = (tags) => {
    // Some tags in customer tags won't be part of the audience tags list
    // Use this variable to filter only the tags we want to add and remove
    const audience_tag_ids = getAudienceTagIds();
    return tags
        ?.map((tag) => {
            if (audience_tag_ids?.includes(tag.id)) return tag.id;
        })
        .filter((id) => id !== undefined);
};

export const ticketsHaveDonationUpsell = (tickets) => {
    return tickets?.some(
        (ticket) => ticket.event?.attribute_WebsiteDonationUpsell === true,
    );
};

export const datePickerToISOString = (dateStr) => {
    // Can be used to convert date picker format to format expected by Spektrix
    const parsed = parse(dateStr, "yyyy-MM-dd", new Date());
    const iso = formatISO(parsed, { representation: "complete" });
    return iso.split(".")[0];
};

export const findDonationByRecognitionName = (donations, name) => {
    return donations?.find((donation) => donation.recognitionName == name);
};

export const getUniqueDates = (instances) => {
    return [
        ...new Set(instances.map((instance) => instance.start.slice(0, 10))),
    ];
};
